import React from 'react';
import { useNavigate } from 'react-router-dom';
import { LoadingLogo } from '../LoadingLogo';
import { PriceChart } from '../PriceChart';
import { LiveRoundTimer } from '../LiveRoundTimer';
import { SVNodeRow } from '../rows/SVNodeRow';
import { ValidatorRow } from '../rows/ValidatorRow';
import { PriceVoteRow } from '../rows/PriceVoteRow';
import { TransactionRow } from '../rows/TransactionRow';
import { AppRow } from '../rows/AppRow';
import { useTopValidators } from '../../hooks/useTopValidators';
import { useTopAppProviders } from '../../hooks/useTopAppProviders';
import { formatNumber } from '../../utils/formatters';

export function OverviewTab({ data, loading, setActiveTab }) {
  const navigate = useNavigate();
  const { validators: topValidators, loading: validatorsLoading } = useTopValidators(50);
  const { apps: topApps, loading: appsLoading } = useTopAppProviders(50);

  const handleSVClick = (sv) => {
    navigate(`/super-validators?highlight=${encodeURIComponent(sv.party)}`);
  };

  const handleValidatorClick = (validator) => {
    navigate(`/validators?highlight=${encodeURIComponent(validator.party)}`);
  };

  return (
    <>
      {/* Price Chart */}
      <PriceChart />

      {/* Live Round Timer */}
      <LiveRoundTimer
        openMiningRound={data.openMiningRound}
        currentRound={data.latestRound?.round}
      />

      {/* Network Stats */}
      <section style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: '1px',
        marginBottom: '32px',
        background: 'var(--border-default)',
        borderRadius: '8px',
        overflow: 'hidden',
      }}
      className="network-stats-grid"
      >
        <div className="stat-card" style={{
          background: 'var(--bg-elevated)',
          padding: '24px',
        }}>
          <div style={{
            fontSize: '11px',
            color: 'var(--text-tertiary)',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            marginBottom: '12px',
            fontFamily: 'var(--font-sans)',
          }}>Total Supply</div>
          <div style={{
            fontSize: '28px',
            fontWeight: '600',
            color: 'var(--text-primary)',
            fontFamily: 'var(--font-mono)',
            letterSpacing: '-1px',
            lineHeight: 1,
          }}>
            {data.networkStats.totalSupply ? formatNumber(data.networkStats.totalSupply) : '—'}
          </div>
          <div style={{
            fontSize: '12px',
            color: 'var(--text-disabled)',
            marginTop: '8px',
            fontFamily: 'var(--font-mono)',
          }}>
            CC
          </div>
        </div>

        <div className="stat-card" style={{
          background: 'var(--bg-elevated)',
          padding: '24px',
        }}>
          <div style={{
            fontSize: '11px',
            color: 'var(--text-tertiary)',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            marginBottom: '12px',
            fontFamily: 'var(--font-sans)',
          }}>Total Rewards</div>
          <div style={{
            fontSize: '28px',
            fontWeight: '600',
            color: 'var(--text-primary)',
            fontFamily: 'var(--font-mono)',
            letterSpacing: '-1px',
            lineHeight: 1,
          }}>
            {formatNumber(data.networkStats.totalRewardsCollected)}
          </div>
          <div style={{
            fontSize: '12px',
            color: 'var(--text-disabled)',
            marginTop: '8px',
            fontFamily: 'var(--font-mono)',
          }}>
            CC distributed
          </div>
        </div>

        <div className="stat-card" style={{
          background: 'var(--bg-elevated)',
          padding: '24px',
        }}>
          <div style={{
            fontSize: '11px',
            color: 'var(--text-tertiary)',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            marginBottom: '12px',
            fontFamily: 'var(--font-sans)',
          }}>Super Validators</div>
          <div style={{
            fontSize: '28px',
            fontWeight: '600',
            color: 'var(--text-primary)',
            fontFamily: 'var(--font-mono)',
            letterSpacing: '-1px',
            lineHeight: 1,
          }}>
            {data.networkStats.svCount || '—'}
          </div>
          <div style={{
            fontSize: '12px',
            color: 'var(--text-disabled)',
            marginTop: '8px',
            fontFamily: 'var(--font-mono)',
          }}>
            governing
          </div>
        </div>

        <div className="stat-card" style={{
          background: 'var(--bg-elevated)',
          padding: '24px',
        }}>
          <div style={{
            fontSize: '11px',
            color: 'var(--text-tertiary)',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            marginBottom: '12px',
            fontFamily: 'var(--font-sans)',
          }}>Validators</div>
          <div style={{
            fontSize: '28px',
            fontWeight: '600',
            color: 'var(--text-primary)',
            fontFamily: 'var(--font-mono)',
            letterSpacing: '-1px',
            lineHeight: 1,
          }}>
            {data.networkStats.validatorCount || '—'}
          </div>
          <div style={{
            fontSize: '12px',
            color: 'var(--text-disabled)',
            marginTop: '8px',
            fontFamily: 'var(--font-mono)',
          }}>
            licensed
          </div>
        </div>
      </section>

      <style>{`
        @media (max-width: 900px) {
          .network-stats-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
        }
        @media (max-width: 500px) {
          .network-stats-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>

      {/* Three Column Layout */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: '12px',
      }}>
        {/* Super Validators Preview */}
        <section style={{
          background: 'var(--bg-elevated)',
          border: '1px solid var(--border-default)',
          borderRadius: '8px',
          overflow: 'hidden',
        }}>
          <div style={{
            padding: '16px 20px',
            borderBottom: '1px solid var(--border-subtle)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
            <h2 style={{
              fontSize: '14px',
              fontWeight: '500',
              color: 'var(--text-primary)',
              fontFamily: 'var(--font-sans)',
            }}>Super Validators</h2>
            <span style={{
              fontSize: '11px',
              color: 'var(--text-tertiary)',
              fontFamily: 'var(--font-mono)',
            }}>{data.svNodes.length} Active</span>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: '36px minmax(0, 1fr) 70px 70px',
            gap: '16px',
            padding: '10px 16px',
            background: 'var(--bg-subtle)',
            fontSize: '10px',
            color: 'var(--text-tertiary)',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            fontFamily: 'var(--font-sans)',
          }}>
            <div>Rank</div>
            <div>SV Node</div>
            <div style={{ textAlign: 'right' }}>Weight</div>
            <div style={{ textAlign: 'right' }}>Status</div>
          </div>

          <div style={{ maxHeight: '350px', overflow: 'auto' }}>
            {data.svNodes.length > 0 ? (
              data.svNodes.slice(0, 10).map((sv, i) => (
                <SVNodeRow key={sv.party || i} sv={sv} rank={i + 1} onClick={handleSVClick} />
              ))
            ) : loading ? (
              <LoadingLogo size={32} />
            ) : (
              <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-tertiary)' }}>
                No SV nodes found
              </div>
            )}
          </div>
          {data.svNodes.length > 10 && (
            <div
              onClick={() => setActiveTab('super validators')}
              style={{
                padding: '12px',
                textAlign: 'center',
                color: 'var(--text-secondary)',
                cursor: 'pointer',
                borderTop: '1px solid var(--border-subtle)',
                fontSize: '13px',
                fontFamily: 'var(--font-sans)',
                transition: 'color 0.15s',
              }}
              onMouseEnter={(e) => e.currentTarget.style.color = 'var(--text-primary)'}
              onMouseLeave={(e) => e.currentTarget.style.color = 'var(--text-secondary)'}
            >
              View all {data.svNodes.length}
            </div>
          )}
        </section>

        {/* Top Validators by Rewards */}
        <section style={{
          background: 'var(--bg-elevated)',
          border: '1px solid var(--border-default)',
          borderRadius: '8px',
          overflow: 'hidden',
        }}>
          <div style={{
            padding: '16px 20px',
            borderBottom: '1px solid var(--border-subtle)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
            <h2 style={{
              fontSize: '14px',
              fontWeight: '500',
              color: 'var(--text-primary)',
              fontFamily: 'var(--font-sans)',
            }}>Top Validators</h2>
            <span style={{
              fontSize: '11px',
              color: 'var(--text-tertiary)',
              fontFamily: 'var(--font-mono)',
            }}>By Rewards</span>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: '36px minmax(0, 1.5fr) 100px 70px',
            gap: '16px',
            padding: '10px 16px',
            background: 'var(--bg-subtle)',
            fontSize: '10px',
            color: 'var(--text-tertiary)',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            fontFamily: 'var(--font-sans)',
          }}>
            <div>Rank</div>
            <div>Validator</div>
            <div style={{ textAlign: 'right' }}>Rewards</div>
            <div style={{ textAlign: 'right' }}>Status</div>
          </div>

          <div style={{ maxHeight: '350px', overflow: 'auto' }}>
            {topValidators.length > 0 ? (
              topValidators.slice(0, 10).map((v, i) => (
                <ValidatorRow key={v.party || i} validator={v} rank={i + 1} showRewards={true} onClick={handleValidatorClick} />
              ))
            ) : validatorsLoading ? (
              <LoadingLogo size={32} />
            ) : (
              <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-tertiary)' }}>
                No validators found
              </div>
            )}
          </div>
          {topValidators.length > 10 && (
            <div
              onClick={() => setActiveTab('validators')}
              style={{
                padding: '12px',
                textAlign: 'center',
                color: 'var(--text-secondary)',
                cursor: 'pointer',
                borderTop: '1px solid var(--border-subtle)',
                fontSize: '13px',
                fontFamily: 'var(--font-sans)',
                transition: 'color 0.15s',
              }}
              onMouseEnter={(e) => e.currentTarget.style.color = 'var(--text-primary)'}
              onMouseLeave={(e) => e.currentTarget.style.color = 'var(--text-secondary)'}
            >
              View all {topValidators.length}
            </div>
          )}
        </section>

        {/* Price Votes */}
        <section style={{
          background: 'var(--bg-elevated)',
          border: '1px solid var(--border-default)',
          borderRadius: '8px',
          overflow: 'hidden',
        }}>
          <div style={{
            padding: '16px 20px',
            borderBottom: '1px solid var(--border-subtle)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
            <h2 style={{
              fontSize: '14px',
              fontWeight: '500',
              color: 'var(--text-primary)',
              fontFamily: 'var(--font-sans)',
            }}>CC Price Votes</h2>
            <span style={{
              fontSize: '11px',
              color: 'var(--text-tertiary)',
              fontFamily: 'var(--font-mono)',
            }}>Median: ${data.networkStats.ccPrice?.toFixed(4) || '—'}</span>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 80px 70px',
            gap: '16px',
            padding: '10px 16px',
            background: 'var(--bg-subtle)',
            fontSize: '10px',
            color: 'var(--text-tertiary)',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            fontFamily: 'var(--font-sans)',
          }}>
            <div>SV Node</div>
            <div style={{ textAlign: 'right' }}>Price</div>
            <div style={{ textAlign: 'right' }}>Updated</div>
          </div>

          <div style={{ maxHeight: '350px', overflow: 'auto' }}>
            {data.networkStats.amuletPriceVotes.length > 0 ? (
              data.networkStats.amuletPriceVotes.map((vote, i) => (
                <PriceVoteRow key={vote.contract_id || i} vote={vote} />
              ))
            ) : loading ? (
              <LoadingLogo size={32} />
            ) : (
              <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-tertiary)' }}>
                No price votes found
              </div>
            )}
          </div>
        </section>

        {/* Top App Providers */}
        <section style={{
          background: 'var(--bg-elevated)',
          border: '1px solid var(--border-default)',
          borderRadius: '8px',
          overflow: 'hidden',
        }}>
          <div style={{
            padding: '16px 20px',
            borderBottom: '1px solid var(--border-subtle)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
            <h2 style={{
              fontSize: '14px',
              fontWeight: '500',
              color: 'var(--text-primary)',
              fontFamily: 'var(--font-sans)',
            }}>Top App Providers</h2>
            <span style={{
              fontSize: '11px',
              color: 'var(--text-tertiary)',
              fontFamily: 'var(--font-mono)',
            }}>By Rewards</span>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: '32px minmax(0, 1.5fr) 100px 70px',
            gap: '12px',
            padding: '10px 16px',
            background: 'var(--bg-subtle)',
            fontSize: '10px',
            color: 'var(--text-tertiary)',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            fontFamily: 'var(--font-sans)',
          }}>
            <div>Rank</div>
            <div>App Provider</div>
            <div style={{ textAlign: 'right' }}>Rewards</div>
            <div style={{ textAlign: 'right' }}>Status</div>
          </div>

          <div style={{ maxHeight: '350px', overflow: 'auto' }}>
            {topApps.length > 0 ? (
              topApps.slice(0, 10).map((app, i) => (
                <AppRow key={app.provider || i} app={app} rank={i + 1} compact={true} showRewards={true} />
              ))
            ) : appsLoading ? (
              <LoadingLogo size={32} />
            ) : (
              <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-tertiary)' }}>
                No app providers found
              </div>
            )}
          </div>
          {topApps.length > 10 && (
            <div
              onClick={() => setActiveTab('apps')}
              style={{
                padding: '12px',
                textAlign: 'center',
                color: 'var(--text-secondary)',
                cursor: 'pointer',
                borderTop: '1px solid var(--border-subtle)',
                fontSize: '13px',
                fontFamily: 'var(--font-sans)',
                transition: 'color 0.15s',
              }}
              onMouseEnter={(e) => e.currentTarget.style.color = 'var(--text-primary)'}
              onMouseLeave={(e) => e.currentTarget.style.color = 'var(--text-secondary)'}
            >
              View all {topApps.length} app providers
            </div>
          )}
        </section>
      </div>

      {/* Transactions Preview */}
      <section style={{
        marginTop: '24px',
        background: 'var(--bg-elevated)',
        border: '1px solid var(--border-default)',
        borderRadius: '8px',
        overflow: 'hidden',
      }}>
        <div style={{
          padding: '16px 20px',
          borderBottom: '1px solid var(--border-subtle)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <h2 style={{
            fontSize: '14px',
            fontWeight: '500',
            color: 'var(--text-primary)',
            fontFamily: 'var(--font-sans)',
          }}>Recent Transactions</h2>
          <span style={{
            fontSize: '11px',
            color: 'var(--text-tertiary)',
            fontFamily: 'var(--font-mono)',
          }}>Latest activity</span>
        </div>

        {/* Desktop header */}
        <div className="hide-mobile" style={{
          display: 'grid',
          gridTemplateColumns: '70px minmax(0, 1fr) 24px minmax(0, 1fr) 90px 70px 60px',
          gap: '12px',
          padding: '10px 16px',
          background: 'var(--bg-subtle)',
          fontSize: '10px',
          color: 'var(--text-tertiary)',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          fontFamily: 'var(--font-sans)',
        }}>
          <div>Type</div>
          <div>From</div>
          <div></div>
          <div>To</div>
          <div style={{ textAlign: 'right' }}>Amount</div>
          <div style={{ textAlign: 'right' }}>Price</div>
          <div style={{ textAlign: 'right' }}>Time</div>
        </div>
        {/* Mobile header */}
        <div className="show-mobile-only" style={{
          display: 'flex',
          padding: '10px 12px',
          background: 'var(--bg-subtle)',
          fontSize: '9px',
          color: 'var(--text-tertiary)',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          fontFamily: 'var(--font-sans)',
        }}>
          <div style={{ width: '55px', flexShrink: 0 }}>Type</div>
          <div style={{ flex: 1, textAlign: 'right' }}>Amount</div>
          <div style={{ width: '60px', textAlign: 'right', flexShrink: 0 }}>Price</div>
          <div style={{ width: '45px', textAlign: 'right', flexShrink: 0 }}>Time</div>
        </div>

        <div style={{ maxHeight: '300px', overflow: 'auto' }}>
          {data.transactions.length > 0 ? (
            data.transactions.slice(0, 8).map((tx, i) => (
              <TransactionRow key={tx.event_id || i} tx={tx} />
            ))
          ) : loading ? (
            <LoadingLogo size={32} />
          ) : (
            <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-tertiary)' }}>
              No recent transactions
            </div>
          )}
        </div>
        {data.transactions.length > 8 && (
          <div
            onClick={() => setActiveTab('transactions')}
            style={{
              padding: '12px',
              textAlign: 'center',
              color: 'var(--text-secondary)',
              cursor: 'pointer',
              borderTop: '1px solid var(--border-subtle)',
              fontSize: '13px',
              fontFamily: 'var(--font-sans)',
              transition: 'color 0.15s',
            }}
            onMouseEnter={(e) => e.currentTarget.style.color = 'var(--text-primary)'}
            onMouseLeave={(e) => e.currentTarget.style.color = 'var(--text-secondary)'}
          >
            View all transactions
          </div>
        )}
      </section>

      {/* CTA Row */}
      <div style={{
        marginTop: '24px',
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: '16px',
      }}>
        {/* Agent Mode CTA */}
        <section
          onClick={() => setActiveTab('chat')}
          style={{
            background: 'var(--bg-elevated)',
            border: '1px solid var(--border-default)',
            borderRadius: '8px',
            padding: '24px',
            cursor: 'pointer',
            transition: 'border-color 0.15s ease',
          }}
          onMouseEnter={(e) => e.currentTarget.style.borderColor = 'var(--border-strong)'}
          onMouseLeave={(e) => e.currentTarget.style.borderColor = 'var(--border-default)'}
        >
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '12px',
          }}>
            <img
              src="/logo_hq_v2.png"
              alt="Cantonstats AI"
              style={{
                width: '28px',
                height: '28px',
                opacity: 0.7,
              }}
            />
            <h3 style={{
              fontSize: '15px',
              fontWeight: '500',
              color: 'var(--text-primary)',
              margin: 0,
              fontFamily: 'var(--font-sans)',
            }}>
              Cantonstats AI
            </h3>
          </div>
          <p style={{
            fontSize: '13px',
            color: 'var(--text-secondary)',
            margin: '0 0 12px 0',
            lineHeight: '1.5',
            fontFamily: 'var(--font-sans)',
          }}>
            Ask questions about the Canton Network, get live stats, and explore documentation
          </p>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            color: 'var(--text-secondary)',
            fontSize: '13px',
            fontFamily: 'var(--font-sans)',
          }}>
            <span>Open AI Assistant</span>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </div>
        </section>

        {/* Connect Node CTA */}
        <section
          onClick={() => navigate('/connect-node')}
          style={{
            background: 'var(--bg-elevated)',
            border: '1px solid var(--border-default)',
            borderRadius: '8px',
            padding: '24px',
            cursor: 'pointer',
            transition: 'border-color 0.15s ease',
          }}
          onMouseEnter={(e) => e.currentTarget.style.borderColor = 'var(--border-strong)'}
          onMouseLeave={(e) => e.currentTarget.style.borderColor = 'var(--border-default)'}
        >
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '12px',
          }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--text-tertiary)" strokeWidth="1.5">
              <rect x="2" y="2" width="20" height="8" rx="2" />
              <rect x="2" y="14" width="20" height="8" rx="2" />
              <circle cx="6" cy="6" r="1.5" fill="var(--text-tertiary)" stroke="none" />
              <circle cx="6" cy="18" r="1.5" fill="var(--text-tertiary)" stroke="none" />
            </svg>
            <h3 style={{
              fontSize: '15px',
              fontWeight: '500',
              color: 'var(--text-primary)',
              margin: 0,
              fontFamily: 'var(--font-sans)',
            }}>
              Connect Node
            </h3>
          </div>
          <p style={{
            fontSize: '13px',
            color: 'var(--text-secondary)',
            margin: '0 0 12px 0',
            lineHeight: '1.5',
            fontFamily: 'var(--font-sans)',
          }}>
            CC reward forecasting and reporting for validators and app providers
          </p>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            color: 'var(--text-secondary)',
            fontSize: '13px',
            fontFamily: 'var(--font-sans)',
          }}>
            <span>Request Access</span>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </div>
        </section>
      </div>
    </>
  );
}
